/**
 * Registro degli incidenti LLM (F-164).
 *
 * Il 16/09/2026 una verifica e' morta con «reason: Required» e non c'era modo
 * di rileggere cosa il modello avesse scritto. Questa pagina e' quel modo: per
 * ogni chiamata a un modello catturata da un catch — trasporto, HTTP,
 * provider, contenuto vuoto, budget, JSON, schema, semantica — il prompt
 * intero e la risposta com'e' arrivata.
 *
 * ⚠️ L'elenco e' leggero e il dettaglio si carica a parte, quando si apre una
 * riga: i prompt pesano decine di kilobyte e contengono posizioni e importi
 * reali di ogni utente. Si leggono uno alla volta, quando servono.
 */

import { useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { formatDateTime, formatNumber } from '@/lib/format';
import { Alert, Badge, Card, Select, Spinner, TableWrap } from '@/ui';
import { useLlmIncident, useLlmIncidents, type IncidentStage, type LlmIncidentRow } from './api';

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : fallback;
}

const WINDOWS = [
  { value: '1', label: 'Ultime 24 ore' },
  { value: '7', label: 'Ultimi 7 giorni' },
  { value: '30', label: 'Ultimi 30 giorni' },
  { value: '90', label: 'Ultimi 90 giorni (ritenzione)' },
];

/** Dove si e' rotto, in parole. Stesso vocabolario del CHECK sulla tabella. */
const STAGE_LABEL: Record<IncidentStage, string> = {
  transport: 'trasporto / timeout',
  http: 'errore HTTP',
  provider: 'errore del provider',
  empty: 'risposta vuota',
  budget: 'budget di token esaurito',
  json: 'non è JSON',
  schema: 'schema non rispettato',
  semantic: 'incoerente nel merito',
};

const STAGE_OPTIONS = [
  { value: '', label: 'Tutti gli stadi' },
  ...(Object.keys(STAGE_LABEL) as IncidentStage[]).map((stage) => ({ value: stage, label: STAGE_LABEL[stage] })),
];

function stageTone(stage: string): 'neutral' | 'negative' | 'warning' | 'accent' {
  if (stage === 'schema' || stage === 'json' || stage === 'semantic') return 'negative';
  if (stage === 'budget' || stage === 'empty') return 'warning';
  return 'neutral';
}

function Meta({ meta }: { meta: Record<string, unknown> | null }) {
  if (!meta) return null;
  const entries = Object.entries(meta).filter(([, value]) => value !== null && value !== undefined);
  if (entries.length === 0) return null;
  return (
    <dl className="admin-incident__meta">
      {entries.map(([key, value]) => (
        <div key={key}>
          <dt>{key}</dt>
          <dd>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

function IncidentDetail({ id }: { id: string }) {
  const detail = useLlmIncident(id);
  if (detail.isPending) return <Spinner label="Carico prompt e risposta" />;
  if (detail.error) {
    return (
      <Alert variant="error" title="Dettaglio non caricato">
        {errorMessage(detail.error, 'Errore imprevisto')}
      </Alert>
    );
  }
  const incident = detail.data;
  return (
    <div className="admin-incident">
      <p className="admin-incident__message">{incident.message}</p>
      <div className="admin__muted">
        {incident.user ? `${incident.user.displayName ?? incident.user.email}` : 'chiamata di sistema'}
        {incident.subjectType && (
          <>
            {' · '}
            {incident.subjectType}
            {incident.subjectId && <code className="admin-incident__code"> {incident.subjectId}</code>}
          </>
        )}
      </div>

      <Meta meta={incident.responseMeta} />

      {/* ⚠️ `finish_reason=length` accanto a un JSON valido e' la firma di un
          output troncato e «riparato» dal provider: e' la prima cosa da
          guardare, e va detta in chiaro invece di lasciarla dedurre. */}
      {incident.finishReason === 'length' && (incident.stage === 'schema' || incident.stage === 'json') && (
        <Alert variant="warning" title="Output troncato dal budget di token">
          Il provider ha chiuso la risposta per <code>finish_reason=length</code>
          {incident.reasoningTokens !== null && (
            <> ({formatNumber(incident.reasoningTokens)} token spesi dal ragionamento)</>
          )}
          : un JSON «valido ma incompleto» qui non è una scelta del modello, è un troncamento riparato. Alza{' '}
          <code>maxTokens</code> o limita il ragionamento per questa operazione.
        </Alert>
      )}

      <h4>Risposta raw {incident.rawChars > 0 && <span className="admin__muted">({formatNumber(incident.rawChars)} caratteri)</span>}</h4>
      {incident.rawResponse ? (
        <pre className="admin-incident__pre">{incident.rawResponse}</pre>
      ) : (
        <p className="admin__muted">Nessun corpo: la chiamata non ha ricevuto una risposta.</p>
      )}

      <h4>Prompt {incident.promptChars > 0 && <span className="admin__muted">({formatNumber(incident.promptChars)} caratteri)</span>}</h4>
      {incident.messages.map((message, index) => (
        <details key={index} className="admin-incident__message-block" open={message.role !== 'system'}>
          <summary>
            <Badge tone={message.role === 'system' ? 'neutral' : message.role === 'user' ? 'accent' : 'warning'}>
              {message.role}
            </Badge>{' '}
            <span className="admin__muted">{formatNumber(message.content.length)} caratteri</span>
          </summary>
          <pre className="admin-incident__pre">{message.content}</pre>
        </details>
      ))}
    </div>
  );
}

function IncidentRow({ row, open, onToggle }: { row: LlmIncidentRow; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr className={open ? 'admin-incident__row admin-incident__row--open' : 'admin-incident__row'}>
        <td>
          <button type="button" className="admin__link" onClick={onToggle} aria-expanded={open}>
            {formatDateTime(row.createdAt)}
          </button>
        </td>
        <td>
          <code>{row.operation}</code>
        </td>
        <td>
          <Badge tone={stageTone(row.stage)}>{STAGE_LABEL[row.stage as IncidentStage] ?? row.stage}</Badge>
        </td>
        <td>
          {row.modelServed ?? row.modelRequested ?? '—'}
          {row.modelServed && row.modelRequested && row.modelServed !== row.modelRequested && (
            <div className="admin__muted">richiesto {row.modelRequested}</div>
          )}
        </td>
        <td>
          {row.finishReason ?? '—'}
          {row.reasoningTokens !== null && <div className="admin__muted">{formatNumber(row.reasoningTokens)} ragionamento</div>}
        </td>
        <td className="admin-incident__cell-message">{row.message}</td>
        <td>{row.user ? (row.user.displayName ?? row.user.email) : <span className="admin__muted">sistema</span>}</td>
      </tr>
      {open && (
        <tr className="admin-incident__detail-row">
          <td colSpan={7}>
            <IncidentDetail id={row.id} />
          </td>
        </tr>
      )}
    </>
  );
}

export function AdminLlmIncidentsPage() {
  const [days, setDays] = useState(7);
  const [operation, setOperation] = useState('');
  const [stage, setStage] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const incidents = useLlmIncidents({ days, operation, stage });

  const operationOptions = [
    { value: '', label: 'Tutte le operazioni' },
    ...(incidents.data?.operations ?? []).map((name) => ({ value: name, label: name })),
  ];

  return (
    <>
      <Card
        title="Incidenti LLM"
        subtitle="Ogni chiamata a un modello catturata da un catch: prompt intero e risposta com'è arrivata. Solo i guasti, mai le chiamate riuscite. Ritenzione 90 giorni."
      >
        <div className="admin__filters admin__filters--three">
          <Select label="Periodo" value={String(days)} onChange={(event) => setDays(Number(event.target.value))} options={WINDOWS} />
          <Select
            label="Operazione"
            value={operation}
            onChange={(event) => setOperation(event.target.value)}
            options={operationOptions}
          />
          <Select label="Stadio" value={stage} onChange={(event) => setStage(event.target.value)} options={STAGE_OPTIONS} />
        </div>
        <Alert variant="warning" title="Contiene dati che non stanno nei log">
          I prompt portano posizioni, importi e liquidità reali degli utenti. Questa pagina esiste per capire perché
          un modello ha risposto male, non per leggere i portafogli altrui.
        </Alert>
      </Card>

      {incidents.isPending ? (
        <Spinner />
      ) : incidents.error ? (
        <Alert variant="error" title="Registro non caricato">
          {errorMessage(incidents.error, 'Errore imprevisto')}
        </Alert>
      ) : incidents.data.total === 0 ? (
        <Card>
          <p>Nessun incidente nel periodo: ogni chiamata a un modello è andata a buon fine, o non c'è stata.</p>
        </Card>
      ) : (
        <Card
          title={`${formatNumber(incidents.data.total)} incidenti`}
          subtitle={
            incidents.data.total > incidents.data.items.length
              ? `Mostrati i ${formatNumber(incidents.data.items.length)} più recenti: restringi il periodo o i filtri.`
              : undefined
          }
          flush
        >
          <TableWrap tall>
            <table className="table table--compact table--sticky-head admin-incident__table">
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Operazione</th>
                  <th>Stadio</th>
                  <th>Modello</th>
                  <th>finish_reason</th>
                  <th>Messaggio</th>
                  <th>Utente</th>
                </tr>
              </thead>
              <tbody>
                {incidents.data.items.map((row) => (
                  <IncidentRow
                    key={row.id}
                    row={row}
                    open={openId === row.id}
                    onToggle={() => setOpenId((current) => (current === row.id ? null : row.id))}
                  />
                ))}
              </tbody>
            </table>
          </TableWrap>
        </Card>
      )}
    </>
  );
}
